/**
 * CSV Formula Injection Prevention Utility
 * Sanitizes data to prevent malicious formula execution in spreadsheet applications
 */

/**
 * List of potentially dangerous formula prefixes that could execute code
 */
const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

/**
 * Sanitizes a single cell value to prevent CSV formula injection
 * @param {any} value - The value to sanitize
 * @returns {string} - Sanitized value safe for CSV export
 */
export const sanitizeCell = (value) => {
  // Handle null, undefined, or non-string values
  if (value === null || value === undefined) {
    return '';
  }
  
  // Convert to string and trim whitespace
  const stringValue = String(value).trim();
  
  // Return empty string for empty values
  if (!stringValue) {
    return '';
  }
  
  // Check if the value starts with a potentially dangerous character
  if (FORMULA_PREFIXES.some(prefix => stringValue.startsWith(prefix))) {
    // Prefix with single quote to prevent formula execution
    // This is the recommended approach by OWASP
    return `'${stringValue}`;
  }
  
  return stringValue;
};

/**
 * Sanitizes an entire data object for CSV export
 * @param {Object} dataObject - Object containing key-value pairs to sanitize
 * @returns {Object} - Sanitized object safe for CSV export
 */
export const sanitizeDataObject = (dataObject) => {
  if (!dataObject || typeof dataObject !== 'object') {
    return {};
  }
  
  const sanitizedObject = {};
  
  for (const [key, value] of Object.entries(dataObject)) {
    sanitizedObject[key] = sanitizeCell(value);
  }
  
  return sanitizedObject;
};

/**
 * Sanitizes an array of data objects for CSV export
 * @param {Array} dataArray - Array of objects to sanitize
 * @returns {Array} - Array of sanitized objects safe for CSV export
 */
export const sanitizeDataArray = (dataArray) => {
  if (!Array.isArray(dataArray)) {
    return [];
  }
  
  return dataArray.map(item => sanitizeDataObject(item));
};

/**
 * Checks if a value contains a CSV formula injection attempt
 * @param {string} value - The input value to check
 * @returns {boolean} - True if the value is potentially dangerous
 */
export const containsFormulaInjection = (value) => {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  return FORMULA_PREFIXES.some(prefix => trimmed.startsWith(prefix));
};